import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Application, ApplicationStatus, ApplicationType } from 'app/models/application';

@Injectable({
  providedIn: 'root'
})
export class ApplicationsService {
  private url = '/api/v1/applications/';

  constructor(private httpClient: HttpClient) {}

  getAll(filters: { type?: ApplicationType; status?: ApplicationStatus; include_user?: boolean } = {}): Observable<Application[]> {
    const params: any = {};
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.include_user) params.include_user = 'true';
    return this.httpClient.get<Application[]>(this.url, { params });
  }

  getByUser(userId: number): Observable<Application[]> {
    return this.httpClient.get<Application[]>(`${this.url}user/${userId}`);
  }

  getById(id: string): Observable<Application> {
    return this.httpClient.get<Application>(`${this.url}${id}`);
  }

  create(userId: number, applicationType: ApplicationType, formData: object): Observable<Application> {
    return this.httpClient.post<Application>(this.url, {
      user_id: userId,
      application_type: applicationType,
      form_data: formData
    });
  }

  createDraft(userId: number, applicationType: ApplicationType, formData: object): Observable<Application> {
    return this.httpClient.post<Application>(this.url, {
      user_id: userId,
      application_type: applicationType,
      form_data: formData,
      is_draft: true
    });
  }

  updateForm(id: string, formData: object): Observable<{ message: string }> {
    return this.httpClient.patch<{ message: string }>(`${this.url}${id}/form`, { form_data: formData });
  }

  updateStatus(id: string, status: ApplicationStatus, userId: number): Observable<{ message: string }> {
    return this.httpClient.patch<{ message: string }>(`${this.url}${id}/status`, { status, user_id: userId });
  }

  vote(id: string, standardisation: number, engagement: number, voter: string): Observable<{ message: string }> {
    return this.httpClient.patch<{ message: string }>(`${this.url}${id}/vote`, {
      standardisation,
      engagement,
      voter
    });
  }

  overrideVote(id: string, voter: string, standardisation: number, engagement: number, editedBy: string): Observable<{ message: string }> {
    return this.httpClient.patch<{ message: string }>(`${this.url}${id}/vote/${voter}`, {
      standardisation,
      engagement,
      edited_by: editedBy
    });
  }

  getScore(applicationId: string): Observable<{score: number, nvotes: number, engVariance: number, standVariance: number}> {
    return this.httpClient.get<{score: number, nvotes: number, engVariance: number, standVariance: number}>(
      `${this.url}${applicationId}/score`
    );
  }

  getVoteDetail(applicationId: string, username?: string): Observable<any> {
    const url = username
      ? `${this.url}${applicationId}/vote-detail/${username}`
      : `${this.url}${applicationId}/vote-detail`;
    return this.httpClient.get<any>(url);
  }

getNote(applicationId: string): Observable<string> {
    return this.httpClient.get<string>(`${this.url}${applicationId}/note`);
  }

  updateNote(applicationId: string, note: string): Observable<{ message: string }> {
    return this.httpClient.patch<{ message: string }>(`${this.url}${applicationId}/note`, { note });
  }

  uploadCv(file: File): Observable<{ cv_file_id: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<{ cv_file_id: string; filename: string }>(
      `${this.url}cv-upload`, formData
    );
  }
}
